import React from 'react'
import styled from 'styled-components'

import { media } from 'styles/media'
import Button from 'components/display/Button'
import XCircleIcon from 'components/icons/XCircle'
import SearchIcon from 'components/icons/Search'
import InvisibleButton from 'components/display/InvisibleButton'
import { TextField } from 'components/form'

import { searchInputZIndex } from '../constants'

type Options = { mobile: boolean; open: boolean }

const Trigger = styled(InvisibleButton)<Options>`
  position: relative;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: text;

  z-index: ${searchInputZIndex};

  display: flex;
  align-items: center;
  appearance: none;
  outline: 0;
  width: 100%;
  background-color: ${({ theme }) => theme['-ds-neutral-100'].value};
  padding: 9px 16px;

  ${media.md`
    padding: 12px 16px;
  `}

  font-size: ${({ theme }) => theme['-ds-font-size-xs']['font-size'].value};
  text-align: left;

  color: black;

  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme['-ds-neutral-400'].value};
  text-transform: none;
  white-space: nowrap;

  em {
    color: ${({ theme }) => theme['-ds-neutral-500'].value};
    font-style: normal;
  }

  svg {
    color: ${({ theme }) => theme['-ds-neutral-700'].value};
    display: inline-block;
    width: 20px;
    height: 20px;
    margin-right: 10px;
  }

  ${({ mobile }) =>
    mobile &&
    `
    height: inherit;
  `}
`

const CloseButton = styled(Button).attrs({
  noBorder: true,
  ghost: true,
  uppercase: false,
})`
  z-index: ${searchInputZIndex};
  padding: 0;
  padding-left: 16px;
`

const Wrapper = styled.div<Options>`
  width: 100%;
  display: flex;
  align-items: center;
  background-color: ${({ theme }) => theme['-ds-neutral-100'].value};
  z-index: ${searchInputZIndex};

  ${({ mobile, open }) =>
    mobile &&
    open &&
    `
    z-index: ${searchInputZIndex + 1};
    position: fixed;
    top: 0;
    left: 0;
    padding: 13px 16px;
    width: 100%;
  `}
`

const ClearButton = styled(InvisibleButton)`
  position: absolute;

  padding: 8px;
  padding-right: 12px;

  ${media.md`
    padding: 11px;
  `}

  top: 0;
  right: 0;
  outline: 0;

  > svg {
    display: inline-block;
    vertical-align: middle;
    margin: 0 !important;
    color: ${({ theme }) => theme['-ds-neutral-400'].value};
  }
`

const AutcompleteWrapper = styled.div`
  display: inline-block;
  width: 100%;
  text-align: left;
  padding-right: 20px;
`

type InputProps = {
  open: boolean
  mobile: boolean
  query: string
  onChange: (e?: React.ChangeEvent<HTMLInputElement>) => any
  onClear: () => any
  onClose: () => any
  onOpen: () => any
  searchInputRef: React.MutableRefObject<HTMLInputElement>
  placeholder: string
}

const Input = ({
  open,
  mobile,
  query,
  onChange,
  onClear,
  onClose,
  onOpen,
  searchInputRef,
  placeholder,
}: InputProps) => {
  return (
    <Wrapper mobile={mobile} open={open}>
      <Trigger
        onClick={onOpen}
        mobile={mobile}
        open={open}
        as={open || query.length > 0 ? 'div' : 'button'}
        aria-label="Search"
        className="qa-search-bar"
      >
        <SearchIcon />
        <span style={{ flexGrow: 1 }}>
          <AutcompleteWrapper>
            <TextField
              key="search-bar-input"
              aria-label={placeholder}
              ref={searchInputRef}
              value={query}
              name="q"
              onChange={onChange}
              placeholder={placeholder}
              autoComplete="off"
              style={{
                border: 'none',
                padding: 0,
                margin: 0,
                background: 'transparent',
              }}
            />
            {query && (
              <ClearButton
                type="button"
                onClick={onClear}
                aria-label="Clear search"
              >
                <XCircleIcon />
              </ClearButton>
            )}
          </AutcompleteWrapper>
        </span>
      </Trigger>

      {open && mobile && <CloseButton onClick={onClose}>Cancel</CloseButton>}
    </Wrapper>
  )
}

export default Input
